import { defineStore, storeToRefs } from 'pinia';
import CryptoJS from 'crypto-js/core';
import 'crypto-js/lib-typedarrays';
import Base64 from 'crypto-js/enc-base64';
import SHA256 from 'crypto-js/sha256';
import { isLocal, useConfigHelper } from '@ilteducation/config';
import { LoginParams } from './domain';
import { tokensByCode } from './api';
import useTokensStore from './use-tokens';
import { hasCodeVerifier, saveCodeVerifier, takeCodeVerifier } from './code-verifier';

const iltCloudUrl = 'http://ilt.cloud';

const { getAppUrl, getUrl } = useConfigHelper();

const createCodeChallenge = () => {
  const codeVerifier = CryptoJS.lib.WordArray.random(32)
    .toString(Base64)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');

  const codeChallenge = SHA256(codeVerifier).toString(Base64).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

  return {
    codeVerifier,
    codeChallenge,
  };
};

const getRedirectUrl = (appName: string, domain?: string, basePath?: string): string => {
  const base = (basePath ?? '').replace(/^\/+/, '');
  if (isLocal()) {
    return `http://${window.location.hostname}:3000/${base}`;
  }

  const redirectUrl = getAppUrl(appName, domain);

  // If we on preview branch the redirect url should contain part of preview url
  return `${redirectUrl}/${base}`;
};

const useAuthentication = defineStore('ilt-auth-authentication', () => {
  const tokensStore = useTokensStore();

  const { tokens } = storeToRefs(tokensStore);

  const createLoginLink = ({
    appName,
    clientId,
    domain,
    reauthenticate,
    hint,
    redirectBasePath,
    localRedirectUrl,
    additionalQuery,
  }: LoginParams): string => {
    const { codeVerifier, codeChallenge } = createCodeChallenge();

    saveCodeVerifier(codeVerifier);

    const redirectUrl = localRedirectUrl ?? getRedirectUrl(appName, domain, redirectBasePath);

    const url = new URL(`${getUrl('auth')}/v1/authorize`);

    if (additionalQuery) {
      Object.entries(additionalQuery).forEach(([key, value]) => {
        if (typeof value === 'string') {
          url.searchParams.set(key, value);
        } else if (Array.isArray(value)) {
          value.forEach((val) => {
            if (val) {
              url.searchParams.set(`${key}[]`, val);
            }
          });
        }
      });
    }

    url.searchParams.set('response_type', 'code');
    url.searchParams.set('scope', 'openid profile');
    url.searchParams.set('client_id', clientId);
    url.searchParams.set('redirect_uri', redirectUrl);
    url.searchParams.set('code_challenge', codeChallenge);
    url.searchParams.set('code_challenge_method', 'S256');

    if (hint) {
      url.searchParams.set('amr_hint', ['pwd', 'sms'].includes(hint) ? hint : `${iltCloudUrl}/auth-method/${hint}`);
    }

    if (reauthenticate) {
      url.searchParams.set('reauthenticate', reauthenticate);
    }

    return url.toString();
  };

  const createEscalationLink = (params: LoginParams): string =>
    createLoginLink({
      ...params,
      reauthenticate: undefined,
      additionalQuery: {
        ...params.additionalQuery,
        acr_values: `${iltCloudUrl}/assurance/secure`,
        id_token_hint: tokens.value?.idToken,
      },
    });

  const authenticateWithCode = async (
    appName: string,
    code: string,
    clientId: string,
    basePath?: string,
    domain?: string,
    localRedirectUrl?: string,
  ): Promise<void> => {
    const codeVerifier = takeCodeVerifier();

    const redirectUrl = localRedirectUrl ?? getRedirectUrl(appName, domain, basePath);

    if (!codeVerifier) {
      console.log('Missing code verifier when authenticating with code.');
      return Promise.reject(new Error('Missing code verifier when authenticating with code.'));
    }

    return tokensByCode({
      code,
      codeVerifier,
      redirectUrl,
      clientId,
    })
      .then(tokensStore.storeAndBroadcastTokens)
      .catch((e) => {
        console.debug('Failed to get token by code', e);
        throw e;
      });
  };

  /** @deprecated only works inside the education app */
  const createActAsLink = (params: LoginParams & { actAs: string }): string =>
    createLoginLink({
      ...params,
      reauthenticate: 'auto',
      hint: 'google',
      additionalQuery: {
        ...params.additionalQuery,
        act_as: params.actAs,
      },
    });

  /**
   * @deprecated Use `createLogoutLink/createLogoutLink` instead
   */
  const createLogoutLink = (redirectUrl: string) => {
    const url = new URL(`${getUrl('auth')}/logout`);
    url.searchParams.set('redirect', redirectUrl);

    return url.toString();
  };

  /**
   * Checking if we can use the auth code flow, meaning that the code has not already been used.
   *
   * We generate a code verifier when redirecting to the auth page,
   * and we clear the code verifier when authenticating with code.
   */
  const canUseAuthCode = async (): Promise<boolean> => hasCodeVerifier();

  return {
    createLoginLink,
    createEscalationLink,
    createActAsLink,
    authenticateWithCode,
    canUseAuthCode,
    createLogoutLink,
  };
});

export default useAuthentication;
